/**
 * Listado maestro de conduces consignados — la pantalla principal del módulo.
 *
 * Mismo flujo que Ventas x Mayor: se listan todos los conduces con sus
 * contadores, y el botón "Detalle" abre el conduce a pantalla completa.
 * Antes había que adivinar el número de conduce en un Autocomplete para ver
 * algo, así que no existía una vista de conjunto.
 *
 * "En piso" es mercancía del consignante (se le puede devolver, no es deuda)
 * y "Por pagar" es lo que ya se vendió y se le debe. Ese segundo importe es
 * exactamente el que el conduce muestra en Cuentas por Pagar, que es donde
 * se le paga: aquí no se cobra ni se paga nada.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx, StatusPill } from "components/financepro";
import { useApi } from "lib/api";
import { estadoConsignacion } from "lib/consignaciones";
import ConduceDetalle from "modules/motores/Consignaciones/conducedetalle";

// Un motor consignado puede pasar meses en piso, así que el rango por
// defecto es el año en curso y no el mes (que dejaría la pantalla vacía).
const inicioAnoISO = () => `${new Date().getFullYear()}-01-01`;
const hoyISO = () => new Date().toISOString().slice(0, 10);

const parseN = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const fmtMoney = (v) =>
  parseN(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const moneyCol = (field, headerName, width = 125) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

const numCol = (field, headerName, width = 90) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
});

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        No hay conduces consignados en el rango seleccionado
      </Typography>
    </Stack>
  );
}

export function estadoConduce(row) {
  const { label, variant } = estadoConsignacion(row);
  return <StatusPill label={label} variant={variant} />;
}

export default function MantenimientoConsignaciones({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioAnoISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [consignantes, setConsignantes] = useState([]);
  const [consignante, setConsignante] = useState(null);
  const [estado, setEstado] = useState("todas");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    (async () => {
      // El combo es opcional: si falla, la pantalla sigue sirviendo sin él.
      // Sin el catch, un servidor caído deja una promesa rechazada suelta.
      try {
        const { ok, data } = await post("/api/cxp_suplidores", {});
        if (ok) setConsignantes(data.suplidores || []);
      } catch (err) {
        setConsignantes([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El motivo de una tabla vacía queda visible en pantalla: un 403 de
  // permisos o un servidor caído eran indistinguibles de "no hay conduces".
  const cargar = async () => {
    setLoading(true);
    setError("");
    try {
      const { status, data } = await post("/api/consignaciones_listar", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        estado,
        ...(consignante ? { suplidor: consignante.id } : {}),
      });
      if (status === 200) {
        setRows(data.facturas || []);
      } else {
        const mensaje =
          data?.Error ||
          (status === 403
            ? "Su rol no tiene acceso a Consignaciones"
            : `No se pudieron cargar los conduces (HTTP ${status})`);
        setRows([]);
        setError(mensaje);
        showAlert({ message: mensaje, color: "error", autoHideDuration: 4000 });
      }
    } catch (err) {
      const mensaje = "No se pudo contactar el servidor: " + (err?.message || err);
      setRows([]);
      setError(mensaje);
      showAlert({ message: mensaje, color: "error", autoHideDuration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const columnas = useMemo(
    () => [
      { field: "nfactura", headerName: "Conduce#", width: 110 },
      { field: "fecha", headerName: "Fecha", width: 105 },
      { field: "suplidornombre", headerName: "Consignante", flex: 1, minWidth: 180 },
      numCol("motores", "Motores"),
      numCol("enpiso", "En piso"),
      numCol("vendidos", "Vendidos"),
      numCol("devueltos", "Devueltos"),
      moneyCol("costopiso", "Costo en piso", 140),
      moneyCol("costovendido", "Costo vendido", 140),
      moneyCol("vplicado", "Pagado", 125),
      moneyCol("porpagar", "Por pagar", 130),
      {
        field: "estado",
        headerName: "Estado",
        width: 140,
        sortable: false,
        renderCell: (params) => estadoConduce(params.row),
      },
      {
        field: "acciones",
        headerName: "",
        width: 130,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setDetalle(params.row)}
            sx={btnOutline(FP.color.accentBlue)}
          >
            Detalle
          </Button>
        ),
      },
    ],
    []
  );

  const totalPiso = rows.reduce((acc, r) => acc + parseN(r.costopiso), 0);
  const totalPorPagar = rows.reduce((acc, r) => acc + parseN(r.porpagar), 0);
  const totalEnPiso = rows.reduce((acc, r) => acc + Number(r.enpiso || 0), 0);

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      {!embedded && (
        <PageHeader
          title="Conduces consignados"
          subtitle="Motores recibidos en consignación, por conduce"
        />
      )}

      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                options={consignantes}
                value={consignante}
                onChange={(_e, v) => setConsignante(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Consignante (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="todas">Todos</MenuItem>
                <MenuItem value="enpiso">Con motores en piso</MenuItem>
                <MenuItem value="porliquidar">Con deuda pendiente</MenuItem>
                <MenuItem value="saldadas">Saldados</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          {error ? (
            <Box sx={{ mb: 1.6 }}>
              <Typography sx={{ color: FP.color.danger, fontWeight: 700, fontSize: "0.9rem" }}>
                {error}
              </Typography>
              <Button onClick={cargar} sx={{ textTransform: "none", fontWeight: 700, px: 0 }}>
                Reintentar
              </Button>
            </Box>
          ) : null}

          <Stack
            direction="row"
            spacing={1.2}
            flexWrap="wrap"
            sx={{ mb: 1.6 }}
            useFlexGap
            alignItems="center"
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}>
              {rows.length} conduces · {totalEnPiso} motores en piso
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
              En piso (no es deuda):{" "}
              <strong style={{ color: FP.color.textPrimary }}>RD$ {fmtMoney(totalPiso)}</strong>
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
              Por pagar:{" "}
              <strong style={{ color: FP.color.danger }}>RD$ {fmtMoney(totalPorPagar)}</strong>
            </Typography>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columnas}
              getRowId={(r) => `${r.nfactura}-${r.suplidor}`}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      <Dialog fullScreen open={!!detalle} onClose={() => setDetalle(null)}>
        {detalle && (
          <ConduceDetalle
            consignacion={detalle}
            onExit={() => {
              setDetalle(null);
              cargar();
            }}
            onChanged={cargar}
          />
        )}
      </Dialog>
    </Box>
  );
}
